import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { REJECT_REASONS, type RejectReason, type SelectionSlot } from '@entities/selection';
import { Button, Modal, Select } from '@shared/ui';

import styles from './Dialogs.module.css';

interface RejectDialogProps {
  slot: SelectionSlot | null;
  /** Идёт ли торг по этому автомобилю: тогда подтверждение в два шага (§34.5). */
  bargaining?: boolean | undefined;
  busy: boolean;
  error: string | null;
  onConfirm: (reason: RejectReason | undefined) => void;
  onClose: () => void;
}

/**
 * Подтверждение «Не подходит» (FE-222, MVP_PRD §34.5).
 *
 * **Без оптимистичного UI и в два шага** (CLAUDE.md §8): действие освобождает
 * слот, закрывает переписку и расходует одну из восьми замен — рисовать
 * результат до ответа сервера нельзя.
 *
 * Причина **необязательна** и приходит кодом из фиксированного списка:
 * свободного текста нет, разбирать его некому. Пользователь имеет право
 * отказаться без объяснений.
 *
 * В фазе торга подтверждение усилено вторым шагом: письма уже ушли, продавец
 * потратил время, и случайное нажатие не должно этого стирать.
 */
export function RejectDialog({
  slot,
  bargaining = false,
  busy,
  error,
  onConfirm,
  onClose,
}: RejectDialogProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const reasonId = useId();
  const [reason, setReason] = useState<RejectReason | ''>('');
  const [acknowledged, setAcknowledged] = useState(false);

  const needsSecondStep = bargaining && !acknowledged;

  return (
    <Modal
      open={slot !== null}
      onClose={() => {
        setReason('');
        setAcknowledged(false);
        onClose();
      }}
      labelledBy={titleId}
      dismissible={!busy}
      className={styles.dialog}
    >
      <h2 id={titleId} className={styles.title}>
        {t('selection.reject.title')}
      </h2>

      {slot === null ? null : (
        <p className={styles.subject}>
          {slot.vehicle.make} {slot.vehicle.model}
          {slot.vehicle.variant === null ? null : ` · ${slot.vehicle.variant}`}
        </p>
      )}

      <p className={styles.text}>{t('selection.reject.text')}</p>

      {bargaining ? <p className={styles.warning}>{t('selection.reject.bargaining')}</p> : null}

      <Select
        id={reasonId}
        label={t('selection.reject.reasonLabel')}
        hint={t('selection.reject.reasonHint')}
        value={reason}
        onChange={(event) => {
          setReason(event.target.value as RejectReason | '');
        }}
      >
        <option value="">{t('selection.reject.reasonEmpty')}</option>
        {REJECT_REASONS.map((code) => (
          <option key={code} value={code}>
            {t(`selection.reject.reason.${code}`)}
          </option>
        ))}
      </Select>

      {error === null ? null : (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}

      <div className={styles.actions}>
        <Button
          variant="secondary"
          disabled={busy}
          onClick={() => {
            setReason('');
            setAcknowledged(false);
            onClose();
          }}
        >
          {t('common.cancel')}
        </Button>
        <Button
          variant="danger"
          disabled={busy}
          onClick={() => {
            if (needsSecondStep) {
              setAcknowledged(true);
              return;
            }
            onConfirm(reason === '' ? undefined : reason);
          }}
        >
          {busy
            ? t('selection.reject.pending')
            : needsSecondStep
              ? t('selection.reject.continue')
              : t('selection.reject.confirm')}
        </Button>
      </div>
    </Modal>
  );
}
